import { axiosApiClient, axiosPublicClient } from "..";
import { handleError } from "../../utils/errors";

const BASE_URL = "/bands";

class BandsService {
  getBands = async ({ page = 1, search, limit = 12, includeInactive = false }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/`, {
        params: {
          page,
          search: search || undefined,
          limit,
          includeInactive,
        },
      });

      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching bands.");
    }
  };

  getBandById = async (id) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${id}`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching band by id.");
    }
  };

  getBandsForDropdown = async () => {
    try {
      const response = await axiosPublicClient.get(`${BASE_URL}/dropdown`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching bands for dropdown.",
      );
    }
  };

  getFeaturedBands = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/featured`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching featured bands.");
    }
  };

  getTopBandsByRevenue = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/top-revenue`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching top bands.");
    }
  };

  createBand = async (requestData) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/`, requestData);
      if (response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating band.");
    }
  };

  editBand = async ({ id, requestData }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/${id}`,
        requestData,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while editing band.");
    }
  };

  deleteBand = async (id) => {
    try {
      const response = await axiosApiClient.delete(`${BASE_URL}/${id}`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting band.");
    }
  };

  toggleBandStatus = async (id) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/${id}/toggle-status`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while toggling band status.");
    }
  };

  // DASHBOARD PRISTUP

  createDashboardAccess = async ({ bandId, username, password }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/${bandId}/dashboard-access`,
        { username, password },
      );
      if (response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating dashboard access.");
    }
  };

  getAdminCredentials = async (bandId) => {
    const response = await axiosApiClient.get(
      `${BASE_URL}/${bandId}/admin-credentials`,
    );
    return response.data.data; // { adminCredentials: { username } }
  };

  resetDashboardPassword = async ({ bandId, newPassword }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/${bandId}/dashboard-password`,
        { newPassword },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while resetting dashboard password.",
      );
    }
  };

  removeDashboardAccess = async (bandId) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/${bandId}/dashboard-access`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while removing dashboard access.");
    }
  };

  // ANALYTICS

  getBandAnalytics = async (id) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${id}/analytics`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching band analytics.");
    }
  };

  getBandProductAnalytics = async ({ id, limit = 10 }) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/${id}/product-analytics`,
        {
          params: {
            limit,
          },
        },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching band product analytics.",
      );
    }
  };

  getProductsByBand = async ({ id, page = 1, limit = 12, search }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${id}/products`, {
        params: {
          page,
          limit,
          search: search || undefined,
        },
      });
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching products by band.");
    }
  };

  // BAND DETAILS
  getBandDetails = async (id) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${id}/details`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching band details.");
    }
  };

  updateBandDetails = async ({ id, data, isFormData }) => {
    try {
      let requestConfig = {};
      let requestData = data;

      if (isFormData) {
        // Za FormData ne postavljamo Content-Type - axios će automatski postaviti multipart/form-data
        requestConfig = {};
        requestData = data; // FormData direktno

        for (let pair of data.entries()) {
          if (pair[0] === "members" || pair[0] === "achievements") {
            try {
              JSON.parse(pair[1]);
            } catch {}
          }
        }
      } else {
        // Za JSON, transformiši podatke kao pre
        requestData = {
          name: data.name,
          description: data.biography, // Mapira biography na description
          biography: data.biography,
          genre: data.genre,
          city: data.city,
          country: data.country,
          founded: parseInt(data.founded) || null,
          label: data.label,

          // Konvertuj members format - osiguraj da je uvek niz
          members: Array.isArray(data.members)
            ? data.members
                .filter((member) => member && (member.name || member.id)) // Ukloni prazne objekte
                .map((member) => ({
                  id: member.id,
                  name: member.name || "",
                  role: member.role || "",
                  bio: member.bio || "",
                  joined: parseInt(member.joined) || null,
                }))
            : [],

          // Konvertuj discography - osiguraj da je uvek niz
          discography: Array.isArray(data.discography)
            ? data.discography
                .filter((album) => album && (album.title || album.name)) // Ukloni prazne objekte
                .map((album) => ({
                  id: album.id,
                  title: album.title || album.name,
                  year: parseInt(album.year) || new Date().getFullYear(),
                  type: album.type || "Studio album",
                  tracks: Array.isArray(album.tracks) ? album.tracks : [],
                }))
            : [],

          // Osiguraj da su svi nizovi stvarno nizovi
          events: Array.isArray(data.events) ? data.events : [],
          gallery: Array.isArray(data.gallery) ? data.gallery : [],
          achievements: Array.isArray(data.achievements)
            ? data.achievements.filter(
                (achievement) =>
                  achievement && achievement.trim && achievement.trim() !== "",
              )
            : [],

          socialLinks: data.socialLinks || {},
          stats: data.stats || {},

          isFeatured: Boolean(data.isFeatured),
          active: data.active !== false,
        };
      }

      const response = await axiosApiClient.put(
        `${BASE_URL}/${id}/details`,
        requestData,
        requestConfig,
      );

      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating band details.");
    }
  };

  // Za javni prikaz (bez autentifikacije)
  getBandPublicDetails = async (id) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/${id}/public-details`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching public band details.",
      );
    }
  };

  // Za pretragu po imenu benda
  getBandByName = async (name) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/by-name/${encodeURIComponent(name)}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching band by name.");
    }
  };

  // ============= BASIC 2 BAND PRODUCT MANAGEMENT ===============

  // GET my products (Basic 2 bands only)
  getMyProducts = async ({ page = 1, limit = 12, search }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/my/products`, {
        params: {
          page,
          limit,
          search: search || undefined,
        },
      });
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching my products.");
    }
  };

  // POST create product (Basic 2 bands only)
  createMyProduct = async (formData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/my/products`,
        formData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating product.");
    }
  };

  // PUT update product (Basic 2 bands only)
  updateMyProduct = async ({ id, requestData }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/my/products/${id}`,
        requestData,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating product.");
    }
  };

  // DELETE product (Basic 2 bands only)
  deleteMyProduct = async (id) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/my/products/${id}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting product.");
    }
  };
}

const bandsService = new BandsService();
export default bandsService;
